import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Loader2, Radar, RefreshCw, XCircle } from 'lucide-react';

const remainingSeconds = (expiresAt) => {
  if (!expiresAt) return 0;
  return Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000));
};

// Searching / no-drivers panel shown while a paid booking waits for a driver.
export const SearchStatusPanel = ({ booking, timeoutSeconds = 90, onRetry, onCancel, busy }) => {
  const [left, setLeft] = useState(() => remainingSeconds(booking.searchExpiresAt));

  useEffect(() => {
    setLeft(remainingSeconds(booking.searchExpiresAt));
    const t = setInterval(() => setLeft(remainingSeconds(booking.searchExpiresAt)), 1000);
    return () => clearInterval(t);
  }, [booking.searchExpiresAt]);

  if (booking.status === 'no_drivers') {
    return (
      <Card className="p-6 mb-6 border-amber-300 bg-amber-50" data-testid="no-drivers-panel">
        <div className="flex items-start gap-3">
          <XCircle className="w-8 h-8 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <h2 className="text-xl font-bold text-amber-900" data-testid="active-booking-status">No drivers available</h2>
            <p className="text-amber-800 mt-1" data-testid="no-drivers-message">
              No drivers available near your pickup. Please try again — the next search covers a wider 10 km radius.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <Button className="flex-1 bg-primary hover:bg-primary-dark" onClick={onRetry} disabled={busy} data-testid="retry-search-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Search again within 10 km
              </Button>
              <Button variant="outline" className="flex-1 border-red-300 text-red-700 hover:bg-red-50" onClick={onCancel}
                disabled={busy} data-testid="cancel-refund-btn">
                Cancel Booking
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const pct = Math.min(100, Math.round(((timeoutSeconds - left) / timeoutSeconds) * 100));
  return (
    <Card className="p-6 mb-6 bg-primary text-white" data-testid="searching-panel">
      <div className="flex items-center gap-3">
        <Radar className="w-8 h-8 animate-pulse" />
        <div className="flex-1">
          <h2 className="text-2xl font-bold" data-testid="active-booking-status">Searching for drivers near you</h2>
          <p className="text-white/90 text-sm" data-testid="searching-subtitle">
            Notifying drivers within {booking.searchRadiusKm} km of your pickup
            {booking.nearbyDriverCount != null && ` · ${booking.nearbyDriverCount} driver${booking.nearbyDriverCount === 1 ? '' : 's'} notified`}
            {booking.femaleDriverOnly && ' · female drivers only'}
          </p>
        </div>
        <span className="text-3xl font-mono font-bold" data-testid="search-countdown">{left}s</span>
      </div>
      <div className="mt-4 h-2 bg-white/30 rounded-full overflow-hidden">
        <div className="h-full bg-white transition-[width] duration-1000" style={{ width: `${pct}%` }} />
      </div>
      <Button variant="outline" className="w-full mt-4 bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white"
        onClick={onCancel} disabled={busy} data-testid="cancel-search-btn">
        Cancel Search
      </Button>
    </Card>
  );
};
